import type { FlagKey } from "@amtana/flags";
import { readWords, useActiveLanguage } from "@amtana/i18n";
import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";

import { allowedNavigation } from "./access-gate.ts";
import type { Installed } from "./route-table.ts";
import type { FlagValues, NavigationEntry, ShownNavigationEntry } from "./web-module.ts";

// The context every rendered tree shares, server or client: the route's own params, every
// installed module's navigation, and the active organization's flags and the session's
// permissions (both undefined until their one source, if any, answers). `create-ui.tsx` and
// `render-page.tsx` both wrap their tree in `FlagScope` over these.

export const ParamsContext = createContext<Readonly<Record<string, string>>>({});
export const NavigationContext = createContext<readonly NavigationEntry[]>([]);
/** The active organization's flags (ADR-006 §7); undefined until the installed flags
 * source, if any, has answered. */
export const FlagValuesContext = createContext<FlagValues | undefined>(undefined);
/** The session's permissions (S12.01); undefined until the installed permissions source, if
 * any, has answered. */
export const PermissionsContext = createContext<readonly string[] | undefined>(undefined);

/** The `:params` of the screen's path pattern. */
export function useRouteParams(): Readonly<Record<string, string>> {
  return useContext(ParamsContext);
}

/** One route param a screen cannot render without. */
export function useRequiredParam(name: string): string {
  const value = useContext(ParamsContext)[name];
  if (value === undefined)
    throw new Error(`Route param "${name}" is missing; check the screen's path pattern.`);
  return value;
}

/** The active organization's flags, straight from the installed flags source; undefined
 * until it answers. Most screens want `useFlag` instead. */
export function useFlagValues(): FlagValues | undefined {
  return useContext(FlagValuesContext);
}

/** One flag's value for the active organization: on, off, or undefined while it is still
 * loading (ADR-006 §7). */
export function useFlag(key: FlagKey): boolean | undefined {
  return useContext(FlagValuesContext)?.[key];
}

/** The session's permissions, straight from the installed permissions source; undefined until
 * it answers (S12.01). */
export function usePermissions(): readonly string[] | undefined {
  return useContext(PermissionsContext);
}

/** Every installed module's navigation entries, in order, minus anything whose flag is not yet
 * on or whose permission is not held (nothing gated draws before its value arrives). */
export function useNavigation(): readonly ShownNavigationEntry[] {
  const navigation = useContext(NavigationContext);
  const flags = useContext(FlagValuesContext);
  const permissions = useContext(PermissionsContext);
  const language = useActiveLanguage();
  return useMemo(
    () =>
      allowedNavigation(navigation, { flags, permissions }).map((entry) => ({
        ...entry,
        label: readWords(entry.label, language),
      })),
    [navigation, flags, permissions, language],
  );
}

/** Reads the flags and permissions sources inside the query provider: each is a hook that
 * queries. */
export function FlagScope({ installed, children }: { installed: Installed; children: ReactNode }) {
  const flagValues = installed.flagsSource?.();
  const permissions = installed.permissionsSource?.();
  return (
    <FlagValuesContext.Provider value={flagValues}>
      <PermissionsContext.Provider value={permissions}>{children}</PermissionsContext.Provider>
    </FlagValuesContext.Provider>
  );
}
